import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useBoundStore } from '@Store/useBoundStore.ts';
import { useCreatePartnerLead } from '@Pages/passwordless/welcome/hooks/useCreatePartnerLead.tsx';
import { useFetchCustomerData } from '@Pages/passwordless/welcome/hooks/useFetchCustomerData.tsx';
import { useNavigationManager } from '@Router/useNavigationManager.ts';
import { useWelcomeAnalytics } from '@Pages/passwordless/welcome/WelcomeAnalytics.tsx';
import { OnboardingFormFields } from '@Types/types.ts';
import { useFormHook, useSetError } from '@Hooks/index.ts';
import { getWelcomeFormScheme } from '@Pages/passwordless/welcome/welcomeForm/WelcomeFormScheme.ts';
import { generateCaptchaToken } from '@Services/init/init-app.service.ts';
import { uploadConsent } from '@Services/api/uploadSnapshot.ts';
import { updateCustomerDataFromServer } from '@Services/init/util.ts';
import { processFieldValidationErrors } from '@Services/errors/processFieldValidationErrors.ts';
import { useCreateLead } from '@Pages/passwordless/welcome/hooks/useCreateLead.tsx';
import { ConsentTypes } from '@Services/api/termsAndConditions';
import { useEmailVerificationNavigationManager } from '@Pages/happyFlowPages/emailVerification/hooks/useEmailVerificationNavigationManager';
import { RoutesMap } from '@Router/router.types';
import { GTMEventLabel } from '@Services/analytics/types';
import { dataLayerHandler } from '@Services/analytics/analytics.service';
import { useLoginMutation, handleLoginResponse } from '@Hooks/api/useLogin';
import { bugsnagClient } from '@Services/observability/initObservability';
import { takeFullPageSnapshot } from '@Services/snapShot/snapshot.service';
import { getLatestExperiences, updateExperiencesCache } from '@Services/convert/useExperience';
import { showResumeApplicationModal } from '@Hooks/modalManager/showResumeApplicationModal.tsx';
import { recoveryStack, getNavigationStackMap } from '@Consts/navigation-stack-map';
import { evaluateRainguard } from '@Services/rainguard/rainguard.service';
import { isDeclinedOnboardingStatus } from '@Utils/onboardingStatus';
import type { RegionCode } from 'google-libphonenumber';
import type { ExtendedUseFormReturn } from '@Hooks/index.ts';
import type { IWelcomeFormFields } from '@Pages/passwordless/welcome/welcomeForm/WelcomeForm.type.ts';

export const useWelcomePageLogic = () => {
  const { firstName, lastName, email } = useBoundStore((state) => state.customer.customerData);
  const { getCustomerData, setCustomerData } = useBoundStore((state) => state);
  const isEmbedded = useBoundStore.getState().onboardingSlice.getIsEmbedded();
  const { editMode, isPartnerFlow, setBufferedPoa } = useBoundStore(
    (state) => state.onboardingSlice
  );
  const queryClient = useQueryClient();
  const { mutateAsync: asyncCreatePartnerLead, isPending: isCreatingPartnerLead } =
    useCreatePartnerLead();
  const { mutateAsync: asyncCreateLead, isPending: isCreatingLead } = useCreateLead();
  const { refetch: asyncGetCustomerData, isLoading: isFetchingCustomerData } =
    useFetchCustomerData();

  const [isLogicLoading, setIsLogicLoading] = useState(false);
  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] = useState(false);

  const { navigateToNextPage, navigateToSpecificPage, navigateToFirstPage } =
    useNavigationManager();
  const { addEmailVerificationToNavStack } = useEmailVerificationNavigationManager();
  const { reportClickTnC, reportClickPrivacyPolicy, reportActOnSignUpPage } = useWelcomeAnalytics();
  const { mutateAsync: loginMutation, isPending: isLoggingIn } = useLoginMutation();
  const setNavigationStack = useBoundStore.getState().onboardingSlice.setNavigationStack;
  const defaultValues: IWelcomeFormFields = {
    [OnboardingFormFields.FIRST_NAME]: firstName || '',
    [OnboardingFormFields.LAST_NAME]: lastName || '',
    [OnboardingFormFields.EMAIL]: email || '',
    [OnboardingFormFields.PASSWORD]: '',
  };

  const { formMethods } = useFormHook({
    formSchema: getWelcomeFormScheme(isEmbedded, !!email),
    formDefaultValues: defaultValues,
    schemaTitle: 'welcome',
  });
  const isValid = formMethods.formState.isValid;
  useSetError({ formMethods: formMethods as ExtendedUseFormReturn<IWelcomeFormFields> });

  const syncCustomerDataToStore = () => {
    const customer = getCustomerData();
    const formData = formMethods.getValues();

    customer.customerData.firstName = formData[OnboardingFormFields.FIRST_NAME];
    customer.customerData.lastName = formData[OnboardingFormFields.LAST_NAME];
    customer.customerData.email = formData[OnboardingFormFields.EMAIL];
    customer.customerData.password = formData[OnboardingFormFields.PASSWORD];
    setCustomerData(customer);
  };

  const closeForgotPasswordModal = () => setIsForgotPasswordModalOpen(false);

  const handleForgotPasswordRecovery = () => {
    const emailValue = formMethods.getValues(OnboardingFormFields.EMAIL);
    closeForgotPasswordModal();
    setNavigationStack(recoveryStack);
    navigateToFirstPage({ email: emailValue });
  };

  const handleSubmit = async () => {
    await generateCaptchaToken();

    reportActOnSignUpPage();

    const formData = formMethods.getValues();

    const experiences = getLatestExperiences();

    const payload = {
      firstName: formData[OnboardingFormFields.FIRST_NAME],
      lastName: formData[OnboardingFormFields.LAST_NAME],
      email: formData[OnboardingFormFields.EMAIL],
      password: formData[OnboardingFormFields.PASSWORD],
      experiences,
    };
    const leadData = isEmbedded
      ? await asyncCreatePartnerLead(payload)
      : await asyncCreateLead(payload);

    updateExperiencesCache(queryClient);

    dataLayerHandler(GTMEventLabel.CLICK_TO_SEND_EMAIL);

    let queryParams = null;

    if (leadData.emailMfaActive && leadData.loginResponseCustomData?.stateToken) {
      const formSection = document.getElementById('welcome-form-section');
      const valueProposalSection = document.getElementById('welcome-value-proposal-section');

      const nodes = [formSection, valueProposalSection].filter(Boolean) as HTMLElement[];

      setIsLogicLoading(true);
      const snapshotBlob = await takeFullPageSnapshot({
        nodes,
      });
      setBufferedPoa({
        type: ConsentTypes.GET_STARTED,
        blob: snapshotBlob,
      });
      addEmailVerificationToNavStack(RoutesMap.WELCOME);
      queryParams = {
        stateToken: leadData.loginResponseCustomData.stateToken,
      };
      syncCustomerDataToStore();
      navigateToNextPage(RoutesMap.WELCOME, queryParams);
      setIsLogicLoading(false);
    } else if (leadData?.requireAuthentication) {
      try {
        const loginResponse = await loginMutation({
          email: formData[OnboardingFormFields.EMAIL],
          password: formData[OnboardingFormFields.PASSWORD],
        });

        // v5 issues the session as the HttpOnly onboardingToken cookie and strips accessToken from
        // the browser response, so a successful login legitimately has no body token. Only flag a
        // response that carries no session token, no MFA challenge and no customer identity.
        if (
          !loginResponse?.stateToken &&
          !loginResponse?.externalId &&
          !isDeclinedOnboardingStatus(loginResponse?.onboardingStatus)
        ) {
          bugsnagClient?.notify(`Error in login without access token: 
            1) do we got login response? ${loginResponse ? 'True' : 'False'}
            2) onboardingStatus: ${loginResponse?.onboardingStatus}
            3) external ID: ${loginResponse?.externalId}
            `);
          setIsForgotPasswordModalOpen(true);
          return;
        }
        setIsLogicLoading(true);
        // Handle login response using shared utility
        await handleLoginResponse({
          loginResponse,
          email: formData[OnboardingFormFields.EMAIL],
          password: formData[OnboardingFormFields.PASSWORD],
          onMfaRequired: (response) => {
            // Handle MFA with state token
            const resetPasswordSlice = useBoundStore.getState().resetPasswordSlice;
            resetPasswordSlice.setEmail(formData[OnboardingFormFields.EMAIL]);
            resetPasswordSlice.setRequestMfaType(response.mfaType!);
            resetPasswordSlice.setMfaDestination(response.mfaDestination!);
            resetPasswordSlice.setStateToken(response.stateToken!);
            navigateToSpecificPage(RoutesMap.LOGIN_OTP_VERIFICATION);
          },
          onMfaWithoutStateToken: () => {
            // Fall back to login page for MFA without state token
            bugsnagClient?.notify('Error in MFA without state token');
            setIsForgotPasswordModalOpen(true);
            // CHECK
          },
          onResume: () => showResumeApplicationModal(),
        });
        setIsLogicLoading(false);
      } catch (error) {
        // Notify the actual error so Bugsnag captures message + stack. Stringifying a
        // native Error yields `{}`, which hid the root cause of these occurrences.
        bugsnagClient?.notify(
          error instanceof Error
            ? error
            : new Error(`useWelcomePageLogic handleSubmit error ${JSON.stringify(error)}`)
        );
      }
      return;
    } else {
      if (leadData?.moreData) {
        const customerData = await asyncGetCustomerData();
        if (customerData?.data?.phoneCountryCode && customerData?.data?.customerData) {
          customerData.data.customerData.phoneCountryCode = customerData.data
            .phoneCountryCode as RegionCode;
        }
        updateCustomerDataFromServer(customerData.data, getCustomerData());
        processFieldValidationErrors(customerData.data?.fieldValidationErrors);
        setNavigationStack(getNavigationStackMap());
      }
      evaluateRainguard();
      setIsLogicLoading(true);
      // Target the specific welcome page sections by ID for reliable snapshot
      const formSection = document.getElementById('welcome-form-section');
      const valueProposalSection = document.getElementById('welcome-value-proposal-section');

      const nodes = [formSection, valueProposalSection].filter(Boolean) as HTMLElement[];

      await uploadConsent({
        type: ConsentTypes.GET_STARTED,
        nodes,
      });
      syncCustomerDataToStore();
      navigateToNextPage(RoutesMap.WELCOME);
      setIsLogicLoading(false);
    }
  };

  const onPrivacyPolicyClick = () => reportClickPrivacyPolicy();
  const onTncClick = () => reportClickTnC();

  const isLoadingData =
    isCreatingLead ||
    isCreatingPartnerLead ||
    isFetchingCustomerData ||
    isLoggingIn ||
    isLogicLoading;

  return {
    formMethods,
    handleSubmit,
    shouldShowExtendedForm: isPartnerFlow,
    onPrivacyPolicyClick,
    onTncClick,
    isEmbedded,
    isLoading: isLoadingData,
    editMode,
    isButtonDisabled: !isValid || isLoadingData,
    elementsDisabled: isPartnerFlow,
    isForgotPasswordModalOpen,
    closeForgotPasswordModal,
    handleForgotPasswordRecovery,
    isLoggingIn,
  };
};
